package com.partejar.app.feature.relacionamentos.presentation

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.toggleable
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonSize
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarDockScaffold
import com.partejar.app.core.ui.components.PartejarList
import com.partejar.app.core.ui.components.PartejarListPhase
import com.partejar.app.core.ui.components.PartejarMotion
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarThemeToggleAction
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.components.partejarPressFeedback
import com.partejar.app.core.ui.components.reducedMotion
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.relacionamentos.data.RelacionamentoFilter
import com.partejar.app.feature.relacionamentos.data.RelacionamentoStage
import com.partejar.app.feature.relacionamentos.data.RelationshipItem
import kotlinx.datetime.LocalDateTime
import kotlinx.coroutines.launch

@Composable
fun RelacionamentosContent(
    state: RelacionamentosUiState,
    filter: RelacionamentoFilter,
    isDark: Boolean,
    onThemeToggle: () -> Unit,
    onFilter: (RelacionamentoFilter) -> Unit,
    onRetry: () -> Unit,
    onLoadNextPage: () -> Unit,
    onOpenDetail: (Long) -> Unit,
    onNew: () -> Unit,
    now: LocalDateTime = currentLocalDateTime()
) {
    val spacing = PartejarTheme.spacing
    val listState = key(filter) { rememberLazyListState() }
    val scope = rememberCoroutineScope()
    val reduced = reducedMotion()
    var loadedIds by remember(filter) { mutableStateOf(emptySet<Long>()) }
    var pendingIds by remember(filter) { mutableStateOf(emptySet<Long>()) }

    PartejarDockScaffold(
        topBar = {
            PartejarTopBar(
                title = "Relacionamentos",
                subtitle = "Com quem você precisa falar",
                leadingContent = {
                    Box(
                        modifier = Modifier
                            .padding(start = spacing.space16)
                            .size(PartejarTheme.dimensions.minimumTouchTarget),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(PartejarIcons.Navigation.relacionamentos, contentDescription = null)
                    }
                },
                actions = { PartejarThemeToggleAction(isDarkTheme = isDark, onClick = onThemeToggle) }
            )
        }
    ) { scaffoldPadding, dockClearance ->
        Column(Modifier.fillMaxSize().padding(scaffoldPadding)) {
            PartejarButton(
                text = "Novo contato",
                onClick = onNew,
                modifier = Modifier.padding(horizontal = spacing.space16).padding(top = spacing.space4),
                size = PartejarButtonSize.COMPACT,
                shape = PartejarShapes.pill,
                leadingIcon = {
                    Icon(
                        PartejarIcons.Actions.add,
                        contentDescription = null,
                        modifier = Modifier.size(PartejarTheme.dimensions.iconSmall)
                    )
                },
                motionEnabled = true
            )

            RelationshipFilters(selected = filter, onSelected = onFilter)

            val content = state as? RelacionamentosUiState.Content
            val rows = remember(content?.items, now) { relationshipListRows(content?.items.orEmpty(), now) }

            LaunchedEffect(content?.items, content?.refreshing) {
                val ids = content?.items.orEmpty().map { it.id }.toSet()
                if (content == null || content.refreshing) {
                    loadedIds = emptySet()
                    pendingIds = emptySet()
                } else {
                    pendingIds = if (ids.containsAll(loadedIds)) {
                        (pendingIds + relationshipPageInsertions(loadedIds, rows)).intersect(ids)
                    } else emptySet()
                    loadedIds = ids
                }
            }

            if (pendingIds.isNotEmpty()) {
                PartejarButton(
                    text = if (pendingIds.size == 1) "Ver novo contato" else "Ver ${pendingIds.size} novos contatos",
                    onClick = {
                        val targetIds = pendingIds
                        scope.launch {
                            val index = rows.indexOfFirst { it is RelationshipListRow.Card && it.item.id in targetIds }
                            if (index >= 0) {
                                if (reduced) listState.scrollToItem(index) else listState.animateScrollToItem(index)
                                pendingIds = pendingIds - targetIds
                            }
                        }
                    },
                    modifier = Modifier
                        .padding(horizontal = spacing.space16, vertical = spacing.space4)
                        .semantics { liveRegion = LiveRegionMode.Polite },
                    variant = PartejarButtonVariant.SECONDARY,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill
                )
            }

            PartejarList(
                items = rows,
                key = RelationshipListRow::key,
                listState = listState,
                phase = when (state) {
                    RelacionamentosUiState.Loading -> PartejarListPhase.LOADING
                    RelacionamentosUiState.Empty -> PartejarListPhase.EMPTY
                    is RelacionamentosUiState.Error -> PartejarListPhase.ERROR
                    is RelacionamentosUiState.Content -> PartejarListPhase.CONTENT
                },
                modifier = Modifier.weight(1f),
                bottomClearance = dockClearance,
                isRefreshing = content?.refreshing == true,
                hasNextPage = content?.hasNextPage == true,
                isLoadingNextPage = content?.isLoadingNextPage == true,
                hasNextPageError = content?.nextPageError != null,
                onLoadNextPage = onLoadNextPage,
                loadingContent = { RelationshipListSkeleton() },
                refreshingContent = {},
                nextPageLoadingContent = { RelationshipCardSkeleton() },
                emptyContent = { EmptyRelationship() },
                errorContent = {
                    RelationshipError(
                        message = (state as? RelacionamentosUiState.Error)?.message.orEmpty(),
                        onRetry = onRetry
                    )
                },
                nextPageErrorContent = {
                    PartejarAlert(
                        title = content?.nextPageError.orEmpty(),
                        tone = PartejarAlertTone.ATTENTION
                    ) {
                        PartejarButton(
                            text = "Tentar novamente",
                            onClick = onLoadNextPage,
                            variant = PartejarButtonVariant.TERTIARY
                        )
                    }
                }
            ) { row ->
                when (row) {
                    is RelationshipListRow.Header -> RelationshipGroupHeader(row.group)
                    is RelationshipListRow.Card -> RelationshipCard(
                        item = row.item,
                        hint = row.hint,
                        onClick = { onOpenDetail(row.item.id) }
                    )
                }
            }
        }
    }
}

@Composable
private fun RelationshipGroupHeader(group: RelationshipListGroup) {
    val spacing = PartejarTheme.spacing
    val attention = group == RelationshipListGroup.NEEDS_YOU
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = spacing.space8)
            .semantics { heading() },
        horizontalArrangement = Arrangement.spacedBy(spacing.space8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (attention) {
            Icon(
                PartejarIcons.Feedback.attention,
                contentDescription = null,
                modifier = Modifier.size(PartejarTheme.dimensions.iconSmall),
                tint = PartejarTheme.colors.attention
            )
        }
        Text(
            group.title,
            style = MaterialTheme.typography.titleSmall,
            color = if (attention) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun RelationshipCard(item: RelationshipItem, hint: RelationshipHint, onClick: () -> Unit) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val subtitle = relationshipCardSubtitle(item)

    PartejarCard(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .alpha(if (item.stage == RelacionamentoStage.LOST) 0.72f else 1f),
        contentPadding = PaddingValues(),
        motionEnabled = true
    ) {
        Column(Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = spacing.space16, end = spacing.space12, top = spacing.space16, bottom = spacing.space12),
                horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                RelationshipAvatar(item.name, dimensions.minimumTouchTarget)
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(spacing.space4)
                ) {
                    Text(
                        item.name,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                    subtitle?.let {
                        Text(
                            it,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
                Icon(
                    imageVector = PartejarIcons.Actions.next,
                    contentDescription = null,
                    modifier = Modifier.size(dimensions.iconMedium),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            RelationshipJourneyBar(
                stage = item.stage,
                modifier = Modifier.padding(start = spacing.space16, end = spacing.space16, bottom = spacing.space12)
            )
            RelationshipHintBand(hint)
        }
    }
}

/** Baby, then due date or gestational age, then maternity; at most two to keep one calm line. */
internal fun relationshipCardSubtitle(item: RelationshipItem): String? = relationshipContextTags(item)
    .sortedBy { listOf("Bebê", "DPP", "Gestação", "Maternidade").indexOf(it.label) }
    .take(2)
    .joinToString(" · ") { tag ->
        when (tag.label) {
            "Bebê" -> "Bebê ${tag.value}"
            "DPP" -> "DPP ${tag.value}"
            else -> tag.value
        }
    }
    .takeIf(String::isNotEmpty)

@Composable
private fun RelationshipListSkeleton() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = PartejarTheme.spacing.space16),
        verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)
    ) {
        repeat(3) { RelationshipCardSkeleton() }
    }
}

@Composable
private fun RelationshipCardSkeleton() {
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space12)
            ) {
                PartejarSkeletonText(Modifier.weight(1f).height(spacing.space20))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.28f).height(spacing.space20))
            }
            PartejarSkeletonText(Modifier.fillMaxWidth(0.68f).height(spacing.space12))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                PartejarSkeleton(Modifier.size(PartejarTheme.dimensions.minimumTouchTarget))
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(spacing.space8)
                ) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.4f).height(spacing.space8))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.62f).height(spacing.space12))
                }
            }
        }
    }
}

@Composable
private fun RelationshipFilters(
    selected: RelacionamentoFilter,
    onSelected: (RelacionamentoFilter) -> Unit
) {
    val spacing = PartejarTheme.spacing
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = spacing.space16, vertical = spacing.space12),
        horizontalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        RelacionamentoFilter.entries.forEach { filter ->
            RelationshipFilter(
                filter = filter,
                selected = filter == selected,
                onClick = { onSelected(filter) }
            )
        }
    }
}

@Composable
private fun RelationshipFilter(
    filter: RelacionamentoFilter,
    selected: Boolean,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val pressed by interactionSource.collectIsPressedAsState()
    val containerColor by animateColorAsState(
        targetValue = if (selected) {
            MaterialTheme.colorScheme.primaryContainer
        } else {
            MaterialTheme.colorScheme.surfaceContainerLow
        },
        animationSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
        label = "relationshipFilterContainer"
    )
    val contentColor by animateColorAsState(
        targetValue = if (selected) {
            MaterialTheme.colorScheme.onPrimaryContainer
        } else {
            MaterialTheme.colorScheme.onSurfaceVariant
        },
        animationSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
        label = "relationshipFilterContent"
    )
    val borderColor by animateColorAsState(
        targetValue = if (selected) {
            MaterialTheme.colorScheme.primary
        } else {
            MaterialTheme.colorScheme.outlineVariant
        },
        animationSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
        label = "relationshipFilterBorder"
    )

    Surface(
        modifier = Modifier
            .heightIn(min = PartejarTheme.dimensions.minimumTouchTarget)
            .partejarPressFeedback(pressed = pressed, enabled = true)
            .clip(PartejarShapes.pill)
            .toggleable(
                value = selected,
                role = Role.RadioButton,
                interactionSource = interactionSource,
                indication = null,
                onValueChange = { onClick() }
            )
            .semantics { this.selected = selected },
        shape = PartejarShapes.pill,
        color = containerColor,
        contentColor = contentColor,
        border = BorderStroke(PartejarTheme.dimensions.strokeThin, borderColor)
    ) {
        Box(
            modifier = Modifier.padding(horizontal = PartejarTheme.spacing.space12),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = filterLabel(filter),
                style = MaterialTheme.typography.labelLarge,
                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium
            )
        }
    }
}

@Composable
private fun EmptyRelationship() {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier.padding(spacing.space24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(spacing.space12)
    ) {
        Icon(
            imageVector = PartejarIcons.Navigation.relacionamentos,
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconLarge),
            tint = MaterialTheme.colorScheme.primary
        )
        Text("Nenhum contato por aqui", style = MaterialTheme.typography.titleMedium)
        Text(
            text = "Novos relacionamentos aparecerão nesta lista.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun RelationshipError(message: String, onRetry: () -> Unit) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier.padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space12)
    ) {
        PartejarAlert(
            title = "Não foi possível carregar",
            tone = PartejarAlertTone.ERROR
        ) {
            Text(message)
        }
        PartejarButton(
            text = "Tentar novamente",
            onClick = onRetry,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

internal data class RelationshipContextTag(val label: String, val value: String)

internal fun relationshipContextTags(item: RelationshipItem): List<RelationshipContextTag> = buildList {
    fun addValue(label: String, value: String?) {
        value?.trim()?.takeIf(String::isNotEmpty)?.let {
            add(RelationshipContextTag(label, it.replace(Regex("\\s+"), " ")))
        }
    }
    item.summary?.trim()?.takeIf(String::isNotEmpty)?.let { summary ->
        if (summary.startsWith("DPP ")) {
            addValue("DPP", formatBrazilianDate(summary.removePrefix("DPP ").trim()) ?: "Não informada")
        } else {
            addValue("Gestação", summary)
        }
    }
    addValue("Bebê", item.babyName)
    addValue("Maternidade", item.maternity)
}

private fun filterLabel(filter: RelacionamentoFilter): String = when (filter) {
    RelacionamentoFilter.ALL -> "Todos"
    RelacionamentoFilter.MEETING -> "Com reunião"
    RelacionamentoFilter.FOLLOW_UP -> "Com retorno"
    RelacionamentoFilter.LOST -> "Perdidos"
}

@Preview(widthDp = 320, heightDp = 720)
@Composable
private fun RelationshipListCompactPreview() = PartejarTheme(ThemeMode.LIGHT) {
    RelacionamentosContent(
        state = RelacionamentosUiState.Content(
            items = listOf(
                RelationshipItem(
                    id = 1,
                    name = "Helena Maria de Freitas",
                    babyName = "Clara",
                    summary = "32s 2d",
                    maternity = "Maternidade Partejar",
                    stage = RelacionamentoStage.WAITING,
                    needsAttention = true,
                    nextFollowUp = "2026-08-27T13:42:00",
                    nextMeeting = null
                )
            )
        ),
        filter = RelacionamentoFilter.FOLLOW_UP,
        isDark = false,
        onThemeToggle = {},
        onFilter = {},
        onRetry = {},
        onLoadNextPage = {},
        onOpenDetail = {},
        onNew = {}
    )
}
